import { getLinkTarget } from "./util.js"

const DURATION = 500
const SETTLE_MAX_MS = 8000
const STABLE_MS = 350
const SETTLE_EPSILON = 2

let activeFrame = null
let settleFrame = null
let settleAbort = null
let lockedLink = null
let scrollBusy = false

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function getScrollTop($target, getOffset) {
  const offset = typeof getOffset === 'function' ? getOffset() : getOffset
  return Math.max(0, Math.floor($target.getBoundingClientRect().top + window.scrollY - offset))
}

function getMaxScroll() {
  return Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
}

// CSS :root { scroll-behavior: smooth } иначе перехватывает наши rAF scrollTo
function scrollToY(y) {
  window.scrollTo({ top: y, left: 0, behavior: 'instant' })
}

function setAnchorScrolling(on) {
  document.documentElement.classList.toggle('is-anchor-scrolling', on)
  document.body.classList.toggle('is-anchor-scrolling', on)
}

function lockLink($link) {
  lockedLink = $link
  $link.setAttribute('aria-disabled', 'true')
  $link.style.pointerEvents = 'none'
}

function unlockLink() {
  if (!lockedLink) return
  lockedLink.removeAttribute('aria-disabled')
  lockedLink.style.pointerEvents = ''
  lockedLink = null
}

function finishScroll() {
  scrollBusy = false
  unlockLink()
  setAnchorScrolling(false)
}

function clearSettle() {
  if (settleFrame) {
    cancelAnimationFrame(settleFrame)
    settleFrame = null
  }
  if (settleAbort) {
    settleAbort()
    settleAbort = null
  }
}

function settleToTarget($target, getOffset) {
  clearSettle()

  const startedAt = performance.now()
  let lastTarget = getScrollTop($target, getOffset)
  let lastDocH = document.documentElement.scrollHeight
  let stableSince = performance.now()

  function snap(now) {
    const targetY = getScrollTop($target, getOffset)
    const docH = document.documentElement.scrollHeight
    const maxScroll = getMaxScroll()
    const effectiveTarget = Math.min(targetY, maxScroll)
    const moved = Math.abs(targetY - lastTarget) > SETTLE_EPSILON || Math.abs(docH - lastDocH) > SETTLE_EPSILON

    if (moved) {
      stableSince = now
      lastTarget = targetY
      lastDocH = docH
    }

    if (Math.abs(window.scrollY - effectiveTarget) > SETTLE_EPSILON) {
      scrollToY(effectiveTarget)
    }

    const stableLongEnough = now - stableSince >= STABLE_MS
    const timedOut = now - startedAt >= SETTLE_MAX_MS
    // сравниваем с effectiveTarget — иначе header offset делает targetY > maxScroll навсегда
    const onTarget = Math.abs(window.scrollY - effectiveTarget) <= SETTLE_EPSILON

    if ((stableLongEnough && onTarget) || timedOut) {
      settleFrame = null
      clearSettle()
      finishScroll()
      return
    }

    settleFrame = requestAnimationFrame(snap)
  }

  const onUserScroll = () => {
    clearSettle()
    finishScroll()
  }
  window.addEventListener('wheel', onUserScroll, { passive: true, once: true })
  window.addEventListener('touchstart', onUserScroll, { passive: true, once: true })
  window.addEventListener('keydown', onUserScroll, { once: true })

  settleAbort = () => {
    window.removeEventListener('wheel', onUserScroll)
    window.removeEventListener('touchstart', onUserScroll)
    window.removeEventListener('keydown', onUserScroll)
  }

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      if (scrollBusy) {
        scrollToY(Math.min(getScrollTop($target, getOffset), getMaxScroll()))
      }
    })
  }

  settleFrame = requestAnimationFrame(snap)
}

function smoothScrollTo($target, getOffset) {
  if (activeFrame) cancelAnimationFrame(activeFrame)
  clearSettle()

  const startY = window.scrollY
  const startTime = performance.now()

  function step(now) {
    const targetY = getScrollTop($target, getOffset)
    const progress = Math.min((now - startTime) / DURATION, 1)

    scrollToY(Math.round(startY + (targetY - startY) * easeInOutCubic(progress)))

    if (progress < 1) {
      activeFrame = requestAnimationFrame(step)
    } else {
      scrollToY(Math.min(targetY, getMaxScroll()))
      activeFrame = null
      settleToTarget($target, getOffset)
    }
  }

  activeFrame = requestAnimationFrame(step)
}

/**
 * @description - плавный скролл
 * @param {HTMLElement} $link
 * @param {Function|number} offset
 * @param {Function} setup
 */
export function scrollLink($link, offset = 0, setup) {
  const $targer = getLinkTarget($link)
  if (!$targer) return

  $link.addEventListener('click', (e) => {
    e.preventDefault()
    if (scrollBusy) return

    const targetY = getScrollTop($targer, offset)
    if (Math.abs(window.scrollY - targetY) <= 1) return

    if (setup) setup($link, $targer)

    scrollBusy = true
    lockLink($link)
    setAnchorScrolling(true)
    smoothScrollTo($targer, offset)
  })
}

//* пример использования: data-scroll-link=".header-container, .section-nav__wrapper"
//* перечисляем селекторы которые зафиксированы с помощью position: fixed
//* получаем селекторы и записываем высоту в offset для корректного скролла к секции

document.addEventListener('DOMContentLoaded', () => {
  $('[data-scroll-link]').each((index, $link) => {
    const selectors = $link.dataset.scrollLink.split(',')
    const targetList = []

    selectors.forEach(selector => {
      selector = selector.trim()
      if (!selector.length) return

      const $target = document.querySelector(selector)
      if (!$target) return

      targetList.push($target)
    })

    scrollLink($link, () => getOffsetTarget(targetList))
  })
})

function getOffsetTarget(targetList) {
  let offset = 0
  $(targetList).each((index, $target) => {
    offset = offset + $target.getBoundingClientRect().height
  })
  return offset
}
